/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * What the runner is about to pay, itemised.
 *
 * The modal has always stated a single figure next to the Pay button. That is
 * enough when the figure is just the fee; it is not enough once tax is added,
 * because a runner who sees ₹600 on the rail and ₹708 at the gateway has no
 * way to tell a tax from a mistake. So the charge is broken out: what the
 * seats cost, what came off, what was taxed, under which heads, and the total.
 *
 * PRESENTATIONAL ONLY. Every figure here arrives as a prop. Nothing is
 * computed from a rate, because the rates and the threshold belong to the
 * server's tax rule — see checkoutQuote.js. The one thing this file decides is
 * what to SHOW, and that decision is driven entirely by which figures it was
 * given.
 *
 * IT RENDERS WITHOUT A QUOTE. With `quote` null — a failed lookup, or a
 * backend that has no tax rule configured — it degrades to the pre-tax
 * itemisation, which is the figure the modal showed before this existed. Tax
 * is additive to a flow that already worked, so its absence must not blank the
 * summary.
 */

/**
 * Is this seat priced under the exemption?
 *
 * Per SEAT, not per cart: the threshold is a per-person admission price, so
 * four ₹400 seats are four exempt admissions rather than one ₹1,600 taxable
 * one. Unknown threshold reads as "not exempt" so nothing is labelled exempt
 * on a guess.
 */
export function isExemptLine(unitAmount, exemptUpto) {
  if (exemptUpto === null || exemptUpto === undefined) return false;
  return Number(unitAmount) <= Number(exemptUpto);
}

export default function CheckoutSummary({
  lines = [], quote = null, total = 0, discount = 0,
  couponCode = null, taxUnknown = false, busy = false, formatFee,
}) {
  if (!lines.length) return null;

  const fee = typeof formatFee === 'function' ? formatFee : value => String(value);

  // The server's figures when there are any, the page's own only as a
  // fallback. Never a mixture: a discount from one source beside a total from
  // another is how a summary comes to disagree with itself.
  const hasQuote = Boolean(quote);
  const shownDiscount = hasQuote ? quote.discount : discount;
  const shownPayable = hasQuote ? quote.payable : Math.max(total - discount, 0);
  const components = hasQuote ? quote.components : [];
  const hasTax = components.length > 0;

  // Only worth its own row once something has moved the total off it. With no
  // discount and no tax, a subtotal line just repeats the total underneath it.
  const showSubtotal = shownDiscount > 0 || hasTax;

  return (
    <div
      className={`evb-sum${busy ? ' evb-sum--busy' : ''}`}
      aria-busy={busy ? 'true' : undefined}
    >
      <p className="evb-sum__lead">Price details</p>

      <dl className="evb-sum__rows">
        {lines.map(line => {
          const exempt = isExemptLine(line.unit, hasQuote ? quote.exemptUpto : null);
          return (
            <div className="evb-sum__row" key={line.id}>
              <dt className="evb-sum__key">
                {line.label}
                <span className="evb-sum__qty">
                  {` · ${fee(line.unit)} × ${line.qty}`}
                </span>
                {/* Said on the row it applies to, because a cart holding one
                    exempt seat and one taxable one otherwise looks like tax
                    was simply missed on half of it. */}
                {exempt && hasTax && (
                  <span className="evb-sum__tag">
                    {`Exempt · ticket up to ${fee(quote.exemptUpto)}`}
                  </span>
                )}
              </dt>
              <dd className="evb-sum__val">{fee(line.amount)}</dd>
            </div>
          );
        })}

        {showSubtotal && (
          <div className="evb-sum__row evb-sum__row--rule">
            <dt className="evb-sum__key">Subtotal</dt>
            <dd className="evb-sum__val">{fee(hasQuote ? quote.gross : total)}</dd>
          </div>
        )}

        {shownDiscount > 0 && (
          <div className="evb-sum__row evb-sum__row--good">
            <dt className="evb-sum__key">
              {couponCode ? `Discount · ${couponCode}` : 'Discount'}
            </dt>
            <dd className="evb-sum__val">{`− ${fee(shownDiscount)}`}</dd>
          </div>
        )}

        {/* The figure the tax was actually charged ON, stated because it is
            not always the subtotal: a discount comes off before tax, and an
            exempt seat is not in it at all. Without it the tax rows look like
            they were worked out from the wrong number. */}
        {hasTax && (
          <div className="evb-sum__row evb-sum__row--rule">
            <dt className="evb-sum__key">Taxable value</dt>
            <dd className="evb-sum__val">{fee(quote.taxable)}</dd>
          </div>
        )}

        {components.map(component => (
          <div className="evb-sum__row" key={component.code || component.label}>
            <dt className="evb-sum__key">{component.label}</dt>
            <dd className="evb-sum__val">{fee(component.amount)}</dd>
          </div>
        ))}
      </dl>

      <div className="evb-sum__total">
        <span className="evb-sum__total-key">Total payable</span>
        <span className="evb-sum__total-val">{fee(shownPayable)}</span>
      </div>

      {/* NOT SHOWN TO THE RUNNER, by decision.
          Place of supply is a mandatory field on the tax INVOICE, and it is
          still recorded against every payment, so nothing is lost by leaving
          it off this screen. But it answers a question nobody buying a race
          entry is asking, and a checkout summary earns its keep by showing
          only what the runner is being charged.

          Kept on the quote (see checkoutQuote.js) because the invoice phase
          needs it; it simply has no line here.

      {hasQuote && quote.placeOfSupply && hasTax && (
        <p className="evb-sum__note">
          {`Place of supply: ${quote.placeOfSupply}`}
        </p>
      )} */}

      {/* THE SERVER ANSWERED, AND THE ANSWER WAS "I CANNOT TAX THIS".
          Shown in the server's own words, because they name the thing that
          has to be fixed. Checkout refuses this same cart for this same
          reason, so saying nothing here would leave the runner to discover it
          at the one moment they had committed to paying. */}
      {hasQuote && quote.taxDetermined === false && quote.taxError && (
        <p className="evb-sum__note evb-sum__note--bad" role="alert">
          {quote.taxError.detail
            || 'This entry cannot be taken just now. Please try again shortly.'}
        </p>
      )}

      {/* Said only when the lookup did not answer at all. The alternative was
          to print nothing, which quietly promises a tax-free total that the
          gateway may be about to disagree with — and a surprise at the
          gateway is the one outcome this whole block exists to prevent. */}
      {taxUnknown && !hasTax && (
        <p className="evb-sum__note evb-sum__note--soft">
          Taxes, where applicable, are confirmed at payment.
        </p>
      )}
    </div>
  );
}
